/**
 * ReservationDetailsModal - Universal reservation details modal for both items and services
 * Fully matches legacy Reservations.js implementation with comprehensive sections
 */

import React, { useState } from 'react';
import { XMarkIcon, UserIcon, TruckIcon, ArrowPathIcon, PencilIcon, CheckIcon, XMarkIcon as CancelIcon, TrashIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import toast from 'react-hot-toast';

const ReservationDetailsModal = ({ isOpen, reservation, onClose, onReprocess, taxRate, canProcessReturns, onReservationUpdate, onDelete, isAdmin }) => {
  // Notes editing state
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState((reservation?.notes || '').trim());
  const [isSavingNotes, setIsSavingNotes] = useState(false);

  // Update notes value when reservation changes
  React.useEffect(() => {
    setNotesValue((reservation?.notes || '').trim());
  }, [reservation?.notes]);

  // Handle reprocess with modal close
  const handleReprocess = async (reservationId) => {
    if (onReprocess) {
      await onReprocess(reservationId);
      onClose(); // Close the modal after reprocessing
    }
  };

  if (!isOpen || !reservation) return null;

  // Debug: Log reservation to check for signature
  console.log('🔍 Reservation Data:', {
    id: reservation.id,
    reservationNumber: reservation.reservationNumber,
    hasSignature: !!reservation.signature,
    signatureLength: reservation.signature?.length,
    signaturePreview: reservation.signature?.substring(0, 50)
  });

  // Save notes function
  const handleSaveNotes = async () => {
    try {
      setIsSavingNotes(true);

      const response = await axios.patch(`/api/reservations/integration/${reservation.id}`, {
        notes: notesValue
      });

      if (response.data.success) {
        toast.success('Notes updated successfully');
        setIsEditingNotes(false);

        // Call parent callback to refresh reservation data if provided
        if (onReservationUpdate) {
          onReservationUpdate(reservation.id, { notes: notesValue });
        }
      } else {
        throw new Error(response.data.message || 'Failed to update notes');
      }
    } catch (error) {
      console.error('Error updating notes:', error);
      toast.error(error.response?.data?.message || 'Failed to update notes');
    } finally {
      setIsSavingNotes(false);
    }
  };

  // Cancel notes editing
  const handleCancelNotes = () => {
    setNotesValue((reservation?.notes || '').trim());
    setIsEditingNotes(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      // Parse the date string as-is, without forcing timezone conversions
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return 'N/A';
    try {
      // Parse dates directly for proper duration calculation
      const start = new Date(startTime);
      const end = new Date(endTime);
      const diffMs = end - start;
      const diffHours = diffMs / (1000 * 60 * 60);
      return `${diffHours.toFixed(2)} hours`;
    } catch (error) {
      return 'N/A';
    }
  };

  const getDurationInHours = (startTime, endTime) => {
    if (!startTime || !endTime) return 0;
    try {
      // Parse dates directly for proper duration calculation
      const start = new Date(startTime);
      const end = new Date(endTime);
      const diffMs = end - start;
      return diffMs / (1000 * 60 * 60);
    } catch (error) {
      return 0;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800';
      case 'COMPLETED':
        return 'bg-blue-100 text-blue-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Reservation Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="space-y-6">
          {/* Header Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {reservation.reservationNumber || `Reservation #${reservation.id}`}
                </h3>
                <p className="text-sm text-gray-600">
                  Created on {formatDate(reservation.createdAt)}
                </p>
              </div>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(reservation.status)}`}>
                {reservation.status}
              </span>
            </div>
          </div>

          {/* Customer Information */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <UserIcon className="h-5 w-5 mr-2" />
              Customer Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">Name</p>
                <p className="text-gray-900">
                  {reservation.customer && reservation.customer.firstName && reservation.customer.lastName ? 
                    `${reservation.customer.firstName} ${reservation.customer.lastName}` : 
                    'Unknown Customer'
                  }
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Phone</p>
                <p className="text-gray-900">{reservation.customer?.phone || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Email</p>
                <p className="text-gray-900">{reservation.customer?.email || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Address</p>
                <p className="text-gray-900">{reservation.customer?.address || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">License Number</p>
                <p className="text-gray-900">{reservation.licenseNumber || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Insurance Information</p>
                <p className="text-gray-900">{reservation.insuranceInfo || 'Not provided'}</p>
              </div>
            </div>

            {/* Second Driver Information (if provided) */}
            {(reservation.secondCustomerFirstName || reservation.secondCustomerLastName ||
              reservation.secondCustomerPhone || reservation.secondCustomerInsuranceProvider || reservation.secondCustomerPolicyNumber) && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h4 className="text-md font-medium text-gray-700 mb-3">Second Driver</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(reservation.secondCustomerFirstName || reservation.secondCustomerLastName) && (
                    <div>
                      <p className="text-sm font-medium text-gray-500">Name</p>
                      <p className="text-gray-900">
                        {[reservation.secondCustomerFirstName, reservation.secondCustomerLastName]
                          .filter(Boolean)
                          .join(' ') || 'Not provided'}
                      </p>
                    </div>
                  )}
                  {reservation.secondCustomerPhone && (
                    <div>
                      <p className="text-sm font-medium text-gray-500">Phone</p>
                      <p className="text-gray-900">{reservation.secondCustomerPhone}</p>
                    </div>
                  )}
                  {reservation.secondCustomerInsuranceProvider && (
                    <div>
                      <p className="text-sm font-medium text-gray-500">Insurance Provider</p>
                      <p className="text-gray-900">{reservation.secondCustomerInsuranceProvider}</p>
                    </div>
                  )}
                  {reservation.secondCustomerPolicyNumber && (
                    <div>
                      <p className="text-sm font-medium text-gray-500">Policy Number</p>
                      <p className="text-gray-900">{reservation.secondCustomerPolicyNumber}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Item/Service Information */}
          {reservation.golfCart && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <TruckIcon className="h-5 w-5 mr-2" />
                Item Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Item Number</p>
                  <p className="text-gray-900">{reservation.golfCart.cartNumber}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Type</p>
                  <p className="text-gray-900">{reservation.golfCart.type}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Hourly Rate</p>
                  <p className="text-gray-900">${reservation.golfCart.hourlyRate}/hr</p>
                </div>
              </div>
            </div>
          )}
          
          {reservation.inventoryItem && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <TruckIcon className="h-5 w-5 mr-2" />
                Inventory Item Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Item Number</p>
                  <p className="text-gray-900">{reservation.inventoryItem.itemNumber}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Variant</p>
                  <p className="text-gray-900">
                    {reservation.inventoryItem.productVariant?.name || 
                     reservation.inventoryItem.productType?.name || 
                     'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Hourly Rate</p>
                  <p className="text-gray-900">
                    ${(reservation.hourlyRateAtBooking || 0).toFixed(2)}/hr
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {reservation.service && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <TruckIcon className="h-5 w-5 mr-2" />
                Service Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Service Name</p>
                  <p className="text-gray-900">{reservation.service.name}</p>
                </div>
                {reservation.service.description && (
                  <div>
                    <p className="text-sm font-medium text-gray-500">Description</p>
                    <p className="text-gray-900">{reservation.service.description}</p>
                  </div>
                )}
              </div>

            </div>
          )}

          {/* Employee Information */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Employee Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">Created By</p>
                <p className="text-gray-900">
                  {reservation.createdByEmployee ? 
                    `${reservation.createdByEmployee.firstName} ${reservation.createdByEmployee.lastName}` : 
                    'Public'
                  }
                </p>
              </div>
              {reservation.processedByEmployee && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Processed Return By</p>
                  <p className="text-gray-900">
                    {reservation.processedByEmployee && reservation.processedByEmployee.firstName && reservation.processedByEmployee.lastName ? 
                      `${reservation.processedByEmployee.firstName} ${reservation.processedByEmployee.lastName}` : 
                      'Unknown'
                    }
                  </p>
                </div>
              )}
              {reservation.reprocessedByEmployee && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Reprocessed By</p>
                  <p className="text-gray-900">
                    {reservation.reprocessedByEmployee && reservation.reprocessedByEmployee.firstName && reservation.reprocessedByEmployee.lastName ? 
                      `${reservation.reprocessedByEmployee.firstName} ${reservation.reprocessedByEmployee.lastName}` : 
                      'Unknown'
                    }
                  </p>
                </div>
              )}
              {reservation.reprocessedAt && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Reprocessed At</p>
                  <p className="text-gray-900">
                    {formatDate(reservation.reprocessedAt)}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Reservation Details */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Reservation Details</h3>

            {(() => {
              // Check if we have multiple sessions by parsing bookedSlots
              let bookedSlots = [];
              let hasMultipleSessions = false;

              try {
                if (reservation.bookedSlots) {
                  if (Array.isArray(reservation.bookedSlots)) {
                    bookedSlots = reservation.bookedSlots;
                  } else if (typeof reservation.bookedSlots === 'string') {
                    bookedSlots = JSON.parse(reservation.bookedSlots);
                  }
                  hasMultipleSessions = bookedSlots.length > 1;
                }
              } catch (e) {
                console.warn('Could not parse bookedSlots:', e);
                hasMultipleSessions = false;
              }

              if (hasMultipleSessions && bookedSlots.length > 0) {
                // Show Session Details for multi-session services
                return (
                  <div className="mb-4">
                    <h4 className="text-md font-medium text-gray-900 mb-3">Session Details</h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="space-y-3">
                        {bookedSlots.map((slot, sessionIndex) => (
                          <div key={`session-detail-${sessionIndex}`} className="bg-white rounded-lg border border-gray-200 p-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                                  <span className="text-blue-600 font-semibold text-sm">{sessionIndex + 1}</span>
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">Session {sessionIndex + 1}</p>
                                  <p className="text-xs text-gray-500">{slot.date}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-medium text-gray-900">{slot.startTime}</p>
                                <p className="text-xs text-gray-500">
                                  {slot.endTime ? `- ${slot.endTime}` : ''}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">{bookedSlots.length}</span> sessions total
                        </p>
                      </div>
                    </div>
                  </div>
                );
              } else {
                // Show regular start/end/duration for single sessions
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Start Time</p>
                      <p className="text-gray-900">{formatDate(reservation.startTime)}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">End Time</p>
                      <p className="text-gray-900">{formatDate(reservation.endTime)}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Duration</p>
                      <p className="text-gray-900">
                        {reservation.reservationDuration
                          ? `${reservation.reservationDuration} ${reservation.reservationDuration === 1 ? 'hour' : 'hours'}`
                          : formatDuration(reservation.startTime, reservation.endTime)
                        }
                      </p>
                    </div>
                  </div>
                );
              }
            })()}

            {/* Exceptions */}
            {(() => {
              let exceptions = [];
              console.log('🔍 Exceptions data:', reservation.exceptions);
              try {
                if (reservation.exceptions) {
                  if (typeof reservation.exceptions === 'string') {
                    exceptions = JSON.parse(reservation.exceptions);
                  } else if (Array.isArray(reservation.exceptions)) {
                    exceptions = reservation.exceptions;
                  }
                }
              } catch (error) {
                console.error('Error parsing exceptions:', error);
              }

              console.log('🔍 Parsed exceptions:', exceptions);
              if (exceptions && exceptions.length > 0) {
                return (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-500 mb-2">Exceptions</p>
                    <div className="flex flex-wrap gap-2">
                      {exceptions.map((exception, index) => (
                        <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          {exception}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              }
              return null;
            })()}

            {/* Number of Passengers */}
            {(() => {
              console.log('🔍 Number of passengers:', reservation.numberOfPassengers);
              if (reservation.numberOfPassengers) {
                return (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-500">Number of Passengers</p>
                    <p className="text-gray-900">{reservation.numberOfPassengers}</p>
                  </div>
                );
              }
              return null;
            })()}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(() => {
                // Use the participantCount field from database - never parse from notes
                const participants = reservation.participantCount;

                if (reservation.service || participants) {
                  return (
                    <div>
                      <p className="text-sm font-medium text-gray-500">Participants</p>
                      <p className="text-gray-900">
                        {participants ?
                          `${participants} ${participants === 1 ? 'person' : 'people'}` :
                          'Not specified'}
                      </p>
                    </div>
                  );
                }
                return null;
              })()}
              {(() => {
                // Show Guest Names for services with collect names enabled
                if (reservation.service && reservation.guestNames) {
                  let guestNames = [];
                  try {
                    guestNames = JSON.parse(reservation.guestNames);
                  } catch (error) {
                    console.error('Error parsing guest names:', error);
                    return null;
                  }

                  if (guestNames && guestNames.length > 0) {
                    return (
                      <div>
                        <p className="text-sm font-medium text-gray-500">Guest Names</p>
                        <div className="text-gray-900">
                          {guestNames.map((guest, index) => (
                            <div key={index} className="text-sm">
                              {guest.firstName} {guest.lastName}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }
                }
                return null;
              })()}
              {(() => {
                // Show Add-ons for services - read from database
                if (reservation.addons && reservation.addons.length > 0) {
                  const addonNames = reservation.addons.map(addon => addon.addonName);
                  
                  return (
                    <div>
                      <p className="text-sm font-medium text-gray-500">Add-ons Selected</p>
                      <p className="text-gray-900">
                        {addonNames.join(', ')}
                      </p>
                    </div>
                  );
                }
                return null;
              })()}
              <div>
                <p className="text-sm font-medium text-gray-500">Deposit Amount</p>
                <p className="text-gray-900">${(reservation.depositAmount || 0).toFixed(2)}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Deposit Type</p>
                <p className="text-gray-900">
                  {reservation.depositTypeCash && reservation.depositTypeCredit ? 'Cash & Credit' :
                   reservation.depositTypeCash ? 'Cash' :
                   reservation.depositTypeCredit ? 'Credit' : 'Not specified'}
                </p>
              </div>
              {reservation.actualReturnTime && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Actual Return Time</p>
                  <p className="text-gray-900">{formatDate(reservation.actualReturnTime)}</p>
                </div>
              )}
              {reservation.overageHours > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Overage Hours</p>
                  <p className="text-gray-900">{(reservation.overageHours || 0).toFixed(2)} hours</p>
                </div>
              )}
            </div>
          </div>

          {/* Financial Information - Complete Legacy Implementation */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Information</h3>
            
            {/* Pricing Breakdown */}
            <div className="mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                {/* Show different pricing info for services vs rentals */}
                {reservation.service && reservation.service.rate !== undefined ? (
                  // Service pricing breakdown with detailed table
                  (() => {
                    // Use the participantCount field from database - never parse from notes
                    const participants = reservation.participantCount || 1;
                    
                    // Use stored booking-time rates when available, fallback to current service rates for older reservations
                    let rate, rateType;
                    if (reservation.serviceRateAtBooking !== null && reservation.serviceRateAtBooking !== undefined && reservation.serviceRateTypeAtBooking) {
                      // Use historical booking rates for accurate financial display
                      rate = reservation.serviceRateAtBooking;
                      rateType = reservation.serviceRateTypeAtBooking;
                    } else {
                      // Fallback to current service rates for older reservations
                      rate = reservation.service.rate || 0;
                      rateType = reservation.service.rateType;
                    }
                    
                    // Apply tiered pricing if enabled
                    try {
                      const service = reservation.service;
                      const enableTieredPricing = service.enableTieredPricing === 'true' || service.enableTieredPricing === true;
                      
                      if (enableTieredPricing && service.pricingTiers) {
                        const pricingTiers = typeof service.pricingTiers === 'string' ? JSON.parse(service.pricingTiers) : service.pricingTiers;
                        const bookingDate = new Date(reservation.createdAt);
                        const bookingLocalDate = new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate());
                        
                        const sortedTiers = pricingTiers
                          .filter(tier => tier.validFrom && tier.validTo)
                          .sort((a, b) => new Date(a.validFrom) - new Date(b.validFrom));
                        
                        for (const tier of sortedTiers) {
                          // Parse dates as local dates to avoid timezone issues
                          const validFromParts = tier.validFrom.split('-');
                          const validToParts = tier.validTo.split('-');

                          // Create dates in local timezone (month is 0-indexed in JS)
                          const validFrom = new Date(
                            parseInt(validFromParts[0]),
                            parseInt(validFromParts[1]) - 1,
                            parseInt(validFromParts[2]),
                            0, 0, 0
                          );

                          const validTo = new Date(
                            parseInt(validToParts[0]),
                            parseInt(validToParts[1]) - 1,
                            parseInt(validToParts[2]),
                            23, 59, 59, 999
                          );

                          if (bookingLocalDate >= validFrom && bookingLocalDate <= validTo) {
                            rate = parseFloat(tier.rate) || rate;
                            rateType = tier.rateType || rateType;
                            break;
                          }
                        }
                      }
                    } catch (e) {
                      // Fall back to base rate
                    }
                    
                    // Calculate service amount
                    let serviceAmount = 0;
                    switch(rateType) {
                      case 'FIXED':
                      case 'PER_ITEM':
                        serviceAmount = rate;
                        break;
                      case 'PER_PERSON':
                        serviceAmount = rate * participants;
                        break;
                      case 'HOURLY':
                        const duration = getDurationInHours(reservation.startTime, reservation.endTime);
                        serviceAmount = duration * rate;
                        break;
                      default:
                        serviceAmount = rate;
                        break;
                    }
                    
                    // Use add-ons from database instead of parsing from notes
                    let selectedAddons = [];
                    if (reservation.addons && reservation.addons.length > 0) {
                      selectedAddons = reservation.addons.map(addon => ({
                        name: addon.addonName,
                        rate: parseFloat(addon.addonRate) || 0,
                        rateType: addon.rateType || 'PER_PERSON',
                        quantity: addon.quantity || 1, // Actual quantity for PER_ITEM addons
                        participants: participants, // Participant count for PER_PERSON addons
                        total: parseFloat(addon.totalAmount) || 0
                      }));
                      
                    }
                    
                    const totalSubtotal = serviceAmount + selectedAddons.reduce((sum, addon) => sum + addon.total, 0);
                    
                    // Calculate tax for the final table
                    let displayTaxRate = reservation.taxRate ?? 0;
                    const taxAmount = totalSubtotal > 0 && displayTaxRate > 0 ? (totalSubtotal * (displayTaxRate / 100)) : 0;
                    const finalTotal = totalSubtotal + taxAmount;
                    
                    return (
                      <div className="space-y-4 col-span-full">
                        {/* Pricing Table */}
                        <div className="overflow-hidden border border-gray-200 rounded-lg">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  {/* Dynamic header based on addon rate types */}
                                  {selectedAddons.some(addon => addon.rateType === 'PER_ITEM') ? 'Quantity' : '# Participants'}
                                </th>
                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Rate</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {/* Individual Sessions - Display each session separately if multiple sessions exist */}
                              {(() => {
                                // Check if we have multiple sessions by parsing bookedSlots
                                let bookedSlots = [];
                                let hasMultipleSessions = false;

                                try {
                                  if (reservation.bookedSlots) {
                                    if (Array.isArray(reservation.bookedSlots)) {
                                      bookedSlots = reservation.bookedSlots;
                                    } else if (typeof reservation.bookedSlots === 'string') {
                                      bookedSlots = JSON.parse(reservation.bookedSlots);
                                    }
                                    hasMultipleSessions = bookedSlots.length > 1;
                                  }
                                } catch (e) {
                                  console.warn('Could not parse bookedSlots:', e);
                                  hasMultipleSessions = false;
                                }

                                if (hasMultipleSessions && bookedSlots.length > 0) {
                                  // Show individual sessions
                                  const perSessionRate = rate;
                                  let perSessionAmount = 0;

                                  if (rateType === 'PER_PERSON') {
                                    perSessionAmount = perSessionRate * participants;
                                  } else {
                                    perSessionAmount = perSessionRate;
                                  }

                                  return bookedSlots.map((slot, sessionIndex) => (
                                    <tr key={`session-${sessionIndex}`}>
                                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                        {reservation.service.name} - Session {sessionIndex + 1}
                                        <div className="text-xs text-gray-500">
                                          {slot.date} at {slot.startTime}
                                        </div>
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-900 text-center">
                                        {participants}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-900 text-center">
                                        {rateType === 'PER_PERSON' ? `$${perSessionRate.toFixed(2)}` :
                                         rateType === 'HOURLY' ? `$${perSessionRate.toFixed(2)}/hour` :
                                         `$${perSessionRate.toFixed(2)}`}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                                        ${perSessionAmount.toFixed(2)}
                                      </td>
                                    </tr>
                                  ));
                                } else {
                                  // Single session - show as before
                                  return (
                                    <tr key="single-session">
                                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                        {reservation.service.name || 'Event Ticket'}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-900 text-center">
                                        {participants}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-900 text-center">
                                        {rateType === 'PER_PERSON' ? `$${rate.toFixed(2)}` :
                                         rateType === 'HOURLY' ? `$${rate.toFixed(2)}/hour` :
                                         `$${rate.toFixed(2)}`}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                                        ${serviceAmount.toFixed(2)}
                                      </td>
                                    </tr>
                                  );
                                }
                              })()}

                              {/* Add-on Rows - Show per session if multiple sessions */}
                              {(() => {
                                // Parse bookedSlots again for add-on display
                                let bookedSlots = [];
                                let hasMultipleSessions = false;

                                try {
                                  if (reservation.bookedSlots) {
                                    if (Array.isArray(reservation.bookedSlots)) {
                                      bookedSlots = reservation.bookedSlots;
                                    } else if (typeof reservation.bookedSlots === 'string') {
                                      bookedSlots = JSON.parse(reservation.bookedSlots);
                                    }
                                    hasMultipleSessions = bookedSlots.length > 1;
                                  }
                                } catch (e) {
                                  hasMultipleSessions = false;
                                }

                                if (hasMultipleSessions && selectedAddons.length > 0) {
                                  // Show add-ons per session
                                  return selectedAddons.flatMap((addon, addonIndex) => {
                                    const addonPerSessionRate = addon.rate;
                                    let addonPerSessionAmount = 0;

                                    if (addon.rateType === 'PER_PERSON') {
                                      addonPerSessionAmount = addonPerSessionRate * participants;
                                    } else if (addon.rateType === 'PER_ITEM') {
                                      addonPerSessionAmount = addonPerSessionRate * addon.quantity;
                                    } else {
                                      addonPerSessionAmount = addonPerSessionRate;
                                    }

                                    return bookedSlots.map((slot, sessionIndex) => (
                                      <tr key={`addon-${addonIndex}-session-${sessionIndex}`}>
                                        <td className="px-4 py-3 text-sm text-gray-900">
                                          {addon.name} - Session {sessionIndex + 1}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900 text-center">
                                          {addon.rateType === 'PER_PERSON' ? participants : addon.quantity}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900 text-center">
                                          ${addonPerSessionRate.toFixed(2)}{addon.rateType === 'PER_ITEM' ? '/item' : addon.rateType === 'FIXED' ? ' (fixed)' : ''}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                                          ${addonPerSessionAmount.toFixed(2)}
                                        </td>
                                      </tr>
                                    ));
                                  });
                                } else {
                                  // Single session add-ons or no multiple sessions
                                  return selectedAddons.map((addon, index) => (
                                    <tr key={`addon-${index}`}>
                                      <td className="px-4 py-3 text-sm text-gray-900">
                                        {addon.name}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-900 text-center">
                                        {addon.rateType === 'PER_PERSON' ? participants : addon.quantity}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-900 text-center">
                                        ${addon.rate.toFixed(2)}{addon.rateType === 'PER_ITEM' ? '/item' : addon.rateType === 'FIXED' ? ' (fixed)' : ''}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                                        ${addon.total.toFixed(2)}
                                      </td>
                                    </tr>
                                  ));
                                }
                              })()}
                              
                              {/* Service Subtotal Row (before discount) */}
                              <tr className="bg-gray-50">
                                <td colSpan="3" className="px-4 py-3 text-sm font-medium text-gray-900 text-right">
                                  Service subtotal:
                                </td>
                                <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right">
                                  ${(() => {
                                    // serviceSubtotal already contains the pre-discount amount
                                    const currentServiceSubtotal = reservation.serviceSubtotal || 0;
                                    return currentServiceSubtotal.toFixed(2);
                                  })()}
                                </td>
                              </tr>

                              {/* Bundle Discount Row */}
                              {reservation.bundleDiscountAmount && reservation.bundleDiscountAmount > 0 && (
                                <tr className="bg-gray-50">
                                  <td colSpan="3" className="px-4 py-3 text-sm font-medium text-green-600 text-right">
                                    Bundle discount ({reservation.bundleDiscountPercentage}%):
                                  </td>
                                  <td className="px-4 py-3 text-sm font-bold text-green-600 text-right">
                                    -${parseFloat(reservation.bundleDiscountAmount).toFixed(2)}
                                  </td>
                                </tr>
                              )}

                              {/* Add-ons Subtotal Row */}
                              {selectedAddons.length > 0 && (
                                <tr className="bg-gray-50">
                                  <td colSpan="3" className="px-4 py-3 text-sm font-medium text-gray-900 text-right">
                                    Add-ons subtotal:
                                  </td>
                                  <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right">
                                    ${(() => {
                                      // Calculate total add-ons amount from displayed items
                                      return selectedAddons.reduce((sum, addon) => {
                                        let addonTotal = 0;
                                        if (addon.rateType === 'PER_PERSON') {
                                          addonTotal = addon.rate * participants;
                                        } else if (addon.rateType === 'PER_ITEM') {
                                          addonTotal = addon.rate * addon.quantity;
                                        } else {
                                          addonTotal = addon.rate;
                                        }

                                        // If multiple sessions, multiply by session count
                                        let bookedSlots = [];
                                        try {
                                          if (reservation.bookedSlots) {
                                            if (Array.isArray(reservation.bookedSlots)) {
                                              bookedSlots = reservation.bookedSlots;
                                            } else if (typeof reservation.bookedSlots === 'string') {
                                              bookedSlots = JSON.parse(reservation.bookedSlots);
                                            }
                                          }
                                        } catch (e) {
                                          bookedSlots = [];
                                        }

                                        const sessionCount = bookedSlots.length > 1 ? bookedSlots.length : 1;
                                        return sum + (addonTotal * sessionCount);
                                      }, 0).toFixed(2);
                                    })()}
                                  </td>
                                </tr>
                              )}

                              {/* Tax Row */}
                              {reservation.taxAmount && reservation.taxAmount > 0 && (
                                <tr className="bg-gray-50">
                                  <td colSpan="3" className="px-4 py-3 text-sm font-medium text-gray-900 text-right">
                                    Tax ({(reservation.taxRate || 0).toFixed(1)}%):
                                  </td>
                                  <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right">
                                    ${parseFloat(reservation.taxAmount).toFixed(2)}
                                  </td>
                                </tr>
                              )}

                              {/* Final Total Row */}
                              <tr className="bg-gray-100 border-t-2">
                                <td colSpan="3" className="px-4 py-3 text-lg font-bold text-gray-900 text-right">
                                  Total:
                                </td>
                                <td className="px-4 py-3 text-lg font-bold text-gray-900 text-right">
                                  ${(() => {
                                    // Calculate total: service subtotal + add-ons subtotal - bundle discount + tax
                                    const currentServiceSubtotal = reservation.serviceSubtotal || 0;
                                    const bundleDiscountAmount = reservation.bundleDiscountAmount || 0;

                                    // serviceSubtotal already contains the pre-discount amount
                                    const serviceTotal = currentServiceSubtotal;

                                    // Calculate add-ons total
                                    const addonsTotal = selectedAddons.reduce((sum, addon) => {
                                      let addonTotal = 0;
                                      if (addon.rateType === 'PER_PERSON') {
                                        addonTotal = addon.rate * participants;
                                      } else if (addon.rateType === 'PER_ITEM') {
                                        addonTotal = addon.rate * addon.quantity;
                                      } else {
                                        addonTotal = addon.rate;
                                      }

                                      // If multiple sessions, multiply by session count
                                      let bookedSlots = [];
                                      try {
                                        if (reservation.bookedSlots) {
                                          if (Array.isArray(reservation.bookedSlots)) {
                                            bookedSlots = reservation.bookedSlots;
                                          } else if (typeof reservation.bookedSlots === 'string') {
                                            bookedSlots = JSON.parse(reservation.bookedSlots);
                                          }
                                        }
                                      } catch (e) {
                                        bookedSlots = [];
                                      }

                                      const sessionCount = bookedSlots.length > 1 ? bookedSlots.length : 1;
                                      return sum + (addonTotal * sessionCount);
                                    }, 0);

                                    const taxAmount = reservation.taxAmount || 0;
                                    const finalTotal = serviceTotal + addonsTotal - bundleDiscountAmount + taxAmount;

                                    return finalTotal.toFixed(2);
                                  })()}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        
                        {/* Deposit Info */}
                        <div className="text-sm mt-4">
                          <p className="text-gray-500">Deposit Paid</p>
                          <p className="text-gray-900 font-medium">${reservation.depositAmount?.toFixed(2) || '0.00'}</p>
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  // Rental pricing layout (existing logic)
                  <>
                    <div>
                      <p className="text-gray-500">Duration</p>
                      <p className="text-gray-900 font-medium">{reservation.reservationDuration} hours</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Hourly Rate</p>
                      <p className="text-gray-900 font-medium">
                        ${(reservation.hourlyRateAtBooking || 0).toFixed(2)}/hr
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Base Amount</p>
                      <p className="text-gray-900 font-medium">
                        ${((reservation.reservationDuration || 0) * (reservation.hourlyRateAtBooking || 0)).toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Deposit Paid</p>
                      <p className="text-gray-900 font-medium">${reservation.depositAmount?.toFixed(2) || '0.00'}</p>
                    </div>
                  </>
                )}
              </div>

              {/* Tax breakdown for item reservations */}
              {!reservation.service && reservation.subtotal !== undefined && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Subtotal</p>
                      <p className="text-gray-900 font-medium">${(reservation.subtotal || 0).toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Tax ({(reservation.taxRate || 0)}%)</p>
                      <p className="text-gray-900 font-medium">${(reservation.taxAmount || 0).toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Total Amount</p>
                      <p className="text-gray-900 font-bold text-lg">${(reservation.totalAmount || 0).toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Additional Charges/Refunds */}
            {(reservation.overageAmount > 0 || reservation.refundAmount > 0) && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <h4 className="text-md font-medium text-gray-900 mb-3">Additional Charges & Refunds</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  {reservation.overageAmount > 0 && (
                    <div>
                      <p className="text-gray-500">Overage Charges</p>
                      <p className="text-orange-600 font-medium">+${(reservation.overageAmount || 0).toFixed(2)}</p>
                      {reservation.overageHours > 0 && (
                        <p className="text-xs text-gray-400">{(reservation.overageHours || 0).toFixed(2)} overage hours</p>
                      )}
                    </div>
                  )}
                  {reservation.refundAmount > 0 && (
                    <div>
                      <p className="text-gray-500">Refund Applied</p>
                      <p className="text-red-600 font-medium">-${(reservation.refundAmount || 0).toFixed(2)}</p>
                      {reservation.refundHours > 0 && (
                        <p className="text-xs text-gray-400">{(reservation.refundHours || 0).toFixed(2)} refund hours</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Payment Breakdown */}
            {reservation.payments && reservation.payments.length > 0 && (() => {
              // Calculate totals across all payments
              const totalCash = reservation.payments.reduce((sum, p) => sum + (p.cashAmount || 0), 0);
              const totalCredit = reservation.payments.reduce((sum, p) => sum + (p.creditAmount || 0), 0);
              const totalSurcharge = reservation.payments.reduce((sum, p) => sum + (p.surchargeAmount || 0), 0);
              const totalCreditCharge = reservation.payments.reduce((sum, p) => sum + (p.creditChargeAmount || 0), 0);
              const grandTotal = reservation.payments.reduce((sum, p) => sum + (p.amount || 0), 0);

              return (
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <h4 className="text-md font-medium text-gray-900 mb-3">Payment Breakdown</h4>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">

                    {/* Individual Payment Sections */}
                    {reservation.payments.map((payment, index) => {
                      const isOverage = index > 0; // First payment is initial, rest are overage
                      const paymentLabel = isOverage ? 'Overage Payment' : 'Initial Payment';

                      // Check if this payment has split details
                      const hasSplitDetails = payment.cashAmount > 0 || payment.creditAmount > 0 || payment.surchargeAmount > 0;

                      return (
                        <div key={payment.id || index} className={index > 0 ? 'mt-4 pt-4 border-t border-blue-300' : ''}>
                          <h5 className="text-sm font-semibold text-gray-700 mb-2">{paymentLabel}</h5>

                          {hasSplitDetails ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                              {payment.cashAmount > 0 && (
                                <div>
                                  <p className="text-gray-500">Cash Payment</p>
                                  <p className="text-gray-900 font-medium">${(payment.cashAmount || 0).toFixed(2)}</p>
                                </div>
                              )}
                              {payment.creditAmount > 0 && (
                                <div>
                                  <p className="text-gray-500">Credit Card Base</p>
                                  <p className="text-gray-900 font-medium">${(payment.creditAmount || 0).toFixed(2)}</p>
                                </div>
                              )}
                              {payment.surchargeAmount > 0 && (
                                <div>
                                  <p className="text-gray-500">Card Surcharge</p>
                                  <p className="text-orange-600 font-medium">+${(payment.surchargeAmount || 0).toFixed(2)}</p>
                                </div>
                              )}
                              {payment.creditChargeAmount > 0 && (
                                <div>
                                  <p className="text-gray-500">Charged on Card</p>
                                  <p className="text-blue-700 font-bold">${(payment.creditChargeAmount || 0).toFixed(2)}</p>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-sm text-gray-600 bg-yellow-50 border border-yellow-200 rounded p-2 mb-2">
                              <p className="font-medium">Payment breakdown details not available</p>
                              <p className="text-xs mt-1">This payment was recorded without cash/credit split details</p>
                            </div>
                          )}

                          <div className="mt-2 pt-2 border-t border-blue-200">
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-gray-600 font-medium">{paymentLabel} Total</span>
                              <span className="text-gray-900 font-semibold">${(payment.amount || 0).toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                      {/* Final Total Section */}
                      <div className="mt-4 pt-4 border-t-2 border-blue-400 bg-blue-100 rounded-md p-3">
                        <h5 className="text-sm font-semibold text-gray-800 mb-3">Final Total</h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm mb-3">
                          {totalCash > 0 && (
                            <div>
                              <p className="text-gray-600">Total Cash</p>
                              <p className="text-gray-900 font-bold">${totalCash.toFixed(2)}</p>
                            </div>
                          )}
                          {totalCredit > 0 && (
                            <div>
                              <p className="text-gray-600">Total Credit</p>
                              <p className="text-gray-900 font-bold">${totalCredit.toFixed(2)}</p>
                            </div>
                          )}
                          {totalSurcharge > 0 && (
                            <div>
                              <p className="text-gray-600">Total Surcharge</p>
                              <p className="text-orange-600 font-bold">${totalSurcharge.toFixed(2)}</p>
                            </div>
                          )}
                        </div>
                        <div className="pt-3 border-t border-blue-300">
                          <div className="flex justify-between items-center">
                            <span className="text-base font-bold text-gray-800">Total Collected</span>
                            <span className="text-xl font-bold text-gray-900">${grandTotal.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
            })()}

          </div>

          {/* Allergies/Dietary Restrictions */}
          {reservation.allergies && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-red-900 mb-4 flex items-center">
                <span className="mr-2 text-red-600">⚠️</span>
                Allergies & Dietary Restrictions
              </h3>
              <div className="bg-white border border-red-200 rounded-md p-3">
                <p className="text-red-800 font-medium">{reservation.allergies}</p>
              </div>
              <p className="text-red-700 text-sm mt-2">
                Please take special care to accommodate these requirements when providing service.
              </p>
            </div>
          )}

          {/* Instagram Handle */}
          {reservation.instagram && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Instagram Handle</h3>
              <p className="text-gray-900 text-lg">
                <a 
                  href={`https://instagram.com/${reservation.instagram.replace(/^@+/, '').trim()}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 hover:underline transition-colors duration-200"
                >
                  {reservation.instagram.startsWith('@') ? reservation.instagram : `@${reservation.instagram}`}
                </a>
              </p>
            </div>
          )}

          {/* Questionnaire Responses */}
          {reservation.questionnaireResponses && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center mb-4">
                <div className="w-5 h-5 text-purple-600 mr-2">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Customer Questionnaire</h3>
              </div>
              <div className="space-y-6">
                {(() => {
                  try {
                    const responses = JSON.parse(reservation.questionnaireResponses);
                    let serviceQuestionnaire = null;
                    
                    // Try to get the original questions from service data
                    if (reservation.service?.questionnaire) {
                      try {
                        serviceQuestionnaire = JSON.parse(reservation.service.questionnaire);
                      } catch (e) {
                        // If service questionnaire is malformed, continue without it
                      }
                    }
                    
                    if (responses.responses && Array.isArray(responses.responses)) {
                      return responses.responses.map((response, index) => {
                        // Try to find the original question
                        let questionText = `Question ${index + 1}`;
                        if (serviceQuestionnaire?.questions) {
                          const originalQuestion = serviceQuestionnaire.questions.find(
                            q => q.id === response.questionId
                          );
                          if (originalQuestion) {
                            questionText = originalQuestion.question;
                          }
                        }
                        
                        return (
                          <div key={index} className="bg-gray-50 rounded-lg p-4 border-l-4 border-purple-300">
                            <div className="text-sm font-semibold text-gray-900 mb-2">
                              {questionText}
                            </div>
                            <div className="text-gray-800">
                              {Array.isArray(response.answer) ? (
                                <div className="flex flex-wrap gap-2">
                                  {response.answer.map((ans, ansIndex) => (
                                    <span key={ansIndex} className="bg-purple-100 text-purple-800 px-2 py-1 rounded-md text-sm">
                                      {ans}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-sm">
                                  {response.answer}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      });
                    }
                    return <p className="text-gray-500 italic">No questionnaire responses found.</p>;
                  } catch (error) {
                    console.error('Error parsing questionnaire responses:', error);
                    return <p className="text-red-500 italic">Error loading questionnaire responses.</p>;
                  }
                })()}
              </div>
            </div>
          )}

          {/* Customer Agreement Signature */}
          {reservation.signature && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Agreement Signature</h3>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-300">
                <img
                  src={reservation.signature}
                  alt="Customer Agreement Signature"
                  className="max-w-full h-auto border border-gray-200 rounded bg-white"
                  style={{ maxHeight: '200px' }}
                />
              </div>
              <p className="text-sm text-gray-500 mt-4">
                Agreement signed on {formatDate(reservation.createdAt)}
              </p>
            </div>
          )}

          {/* Driver Signatures */}
          {(reservation.firstDriverSignature || reservation.secondDriverSignature) && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Driver Signatures</h3>

              <div className="space-y-6">
                {/* First Driver Signature */}
                {reservation.firstDriverSignature && (
                  <div>
                    <h4 className="text-md font-medium text-gray-700 mb-3">
                      First Driver - {reservation.customer?.firstName} {reservation.customer?.lastName}
                    </h4>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                      <p className="text-sm text-blue-900 font-medium">
                        "I have not consumed any alcohol in the last 6 hours"
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-300">
                      <img
                        src={reservation.firstDriverSignature}
                        alt="First Driver Signature"
                        className="max-w-full h-auto border border-gray-200 rounded bg-white"
                        style={{ maxHeight: '200px' }}
                      />
                    </div>
                  </div>
                )}

                {/* Second Driver Signature */}
                {reservation.secondDriverSignature && (
                  <div>
                    <h4 className="text-md font-medium text-gray-700 mb-3">
                      Second Driver - {reservation.secondCustomerFirstName} {reservation.secondCustomerLastName}
                    </h4>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                      <p className="text-sm text-blue-900 font-medium">
                        "I have not consumed any alcohol in the last 6 hours"
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-300">
                      <img
                        src={reservation.secondDriverSignature}
                        alt="Second Driver Signature"
                        className="max-w-full h-auto border border-gray-200 rounded bg-white"
                        style={{ maxHeight: '200px' }}
                      />
                    </div>
                  </div>
                )}
              </div>

              <p className="text-sm text-gray-500 mt-4">
                Driver signatures captured on {formatDate(reservation.createdAt)}
              </p>
            </div>
          )}

          {/* Notes - Editable */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Notes</h3>
              {!isEditingNotes && (
                <button
                  onClick={() => setIsEditingNotes(true)}
                  className="text-blue-600 hover:text-blue-800 flex items-center space-x-1 text-sm"
                >
                  <PencilIcon className="h-4 w-4" />
                  <span>Edit</span>
                </button>
              )}
            </div>

            {isEditingNotes ? (
              <div className="space-y-3">
                <textarea
                  value={notesValue}
                  onChange={(e) => setNotesValue(e.target.value)}
                  placeholder="Add notes about this reservation..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical"
                />
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleSaveNotes}
                    disabled={isSavingNotes}
                    className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center space-x-1"
                  >
                    <CheckIcon className="h-4 w-4" />
                    <span>{isSavingNotes ? 'Saving...' : 'Save'}</span>
                  </button>
                  <button
                    onClick={handleCancelNotes}
                    disabled={isSavingNotes}
                    className="bg-gray-300 hover:bg-gray-400 disabled:bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm font-medium flex items-center space-x-1"
                  >
                    <CancelIcon className="h-4 w-4" />
                    <span>Cancel</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="min-h-[50px]">
                {notesValue ? (
                  <p className="text-gray-900 whitespace-pre-wrap">{notesValue.trim()}</p>
                ) : (
                  <p className="text-gray-500 italic">No notes added yet. Click "Edit" to add notes.</p>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between pt-4">
            <div>
              {/* Reprocess Button - Admin only for completed reservations */}
              {canProcessReturns && reservation.status === 'COMPLETED' && (
                <button
                  onClick={() => handleReprocess(reservation.id)}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-2 rounded-lg font-medium"
                >
                  Reprocess Reservation
                </button>
              )}
              {/* Restore Button - Admin only for cancelled/completed reservations */}
              {canProcessReturns && (reservation.status === 'CANCELLED' || reservation.status === 'COMPLETED') && (
                <button
                  onClick={() => handleReprocess(reservation.id)}
                  className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium mr-2"
                >
                  <ArrowPathIcon className="w-4 h-4 mr-2" />
                  {reservation.status === 'CANCELLED' ? 'Reactivate Reservation' : 'Reactivate Reservation'}
                </button>
              )}

              {/* Delete Button - Admin only for cancelled reservations */}
              {isAdmin && reservation.status === 'CANCELLED' && onDelete && (
                <button
                  onClick={() => {
                    if (window.confirm('Are you sure you want to permanently delete this reservation? This action cannot be undone.')) {
                      onDelete(reservation.id);
                      onClose(); // Close modal after deletion
                    }
                  }}
                  className="inline-flex items-center bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-medium"
                >
                  <TrashIcon className="w-4 h-4 mr-2" />
                  Delete Reservation
                </button>
              )}
            </div>
            <button
              onClick={onClose}
              className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-6 py-2 rounded-lg font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReservationDetailsModal;